'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import { useSponsorships } from '@sola/supporter-sdk/hooks/commitments.ts';
import { NameFormats, useSupporterName } from '@sola/supporter-sdk/hooks/details.ts';
export interface GreetingLanguage {
  key: string;
  greeting: string;
  name: string;
}
interface SelectedGreeting {
  countryKey: string;
  greetingKey: string;
}
export const getGreetingCountryCodeForParticipant = (country?: string | null, localId?: string): string => countryToIso(country, localId);
const getRandomIndex = (length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return Math.floor(Math.random() * length);
};

// Shouldn't ever use this - just a defensive fallback
const defaultGreeting: GreetingLanguage = {
  key: 'US',
  greeting: 'Hello',
  name: 'English'
};
export const selectGreetingLanguage = (languages: GreetingLanguage[], sponsoredChildCountryCodes: ReadonlySet<string>, getRandomLanguageIndex: (length: number) => number = getRandomIndex): GreetingLanguage => {
  const matchingLanguages = languages.filter(language => sponsoredChildCountryCodes.has(language.key));
  const candidateLanguages = matchingLanguages.length > 0 ? matchingLanguages : languages;
  return candidateLanguages[getRandomLanguageIndex(candidateLanguages.length)] ?? defaultGreeting;
};
const mapGreetingLanguages = (languagesMap: Record<string, Omit<GreetingLanguage, 'key'>>): GreetingLanguage[] => {
  return Object.entries(languagesMap).map(([key, value]) => ({
    key,
    greeting: value.greeting,
    name: value.name
  }));
};
export const useHomeGreeting = () => {
  const $ = _c(26);
  const {
    t
  } = useTranslation("greetings");
  const {
    name: supporterName,
    isError: isNameError,
    isSuccess: isNameSuccess,
    isFetching: isNameFetching
  } = useSupporterName(NameFormats.preferred);
  const {
    sponsorships,
    isLoading: isLoadingSponsorships
  } = useSponsorships();
  const [selectedGreeting, setSelectedGreeting] = useState(null);
  const languages = mapGreetingLanguages(t("greetings:languages", {
    returnObjects: true
  }) as Record<string, Omit<GreetingLanguage, 'key'>>);
  const isSupporterNameReady = isNameSuccess && !isNameFetching;
  const isGreetingReady = isSupporterNameReady && !isLoadingSponsorships;
  let t0;
  if ($[0] !== sponsorships) {
    const sponsoredChildCountryCodes = new Set(sponsorships.map(_temp).filter(_temp2));
    t0 = [...sponsoredChildCountryCodes].toSorted().join("|");
    $[0] = sponsorships;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const countryKey = t0;
  let t1;
  let t2;
  if ($[2] !== countryKey || $[3] !== isGreetingReady || $[4] !== t) {
    t1 = () => {
      if (!isGreetingReady) {
        return;
      }
      setSelectedGreeting(current => {
        if (current?.countryKey === countryKey) {
          return current;
        }
        const greetingLanguages = mapGreetingLanguages(t("greetings:languages", {
          returnObjects: true
        }) as Record<string, Omit<GreetingLanguage, 'key'>>);
        const countryCodes = new Set(countryKey === "" ? [] : countryKey.split("|"));
        return {
          countryKey,
          greetingKey: selectGreetingLanguage(greetingLanguages, countryCodes).key
        };
      });
    };
    t2 = [countryKey, isGreetingReady, t];
    $[2] = countryKey;
    $[3] = isGreetingReady;
    $[4] = t;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== t) {
    t3 = t("greetings:subtitle");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const subtitle = t3;
  let t4;
  if ($[9] !== t) {
    t4 = t("greetings:explanationIntro");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const explanationIntro = t4;
  let t5;
  if ($[11] !== t) {
    t5 = languageName => t("greetings:flagTooltip", {
      language: languageName
    });
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const getFlagTooltip = t5;
  const hasSelectedCurrentGreeting = selectedGreeting?.countryKey === countryKey;
  const resolvedLanguage = languages.find(language => language.key === selectedGreeting?.greetingKey) ?? defaultGreeting;
  let t6;
  if ($[13] !== resolvedLanguage.key || $[14] !== t) {
    t6 = resolvedLanguage.key === defaultGreeting.key ? "" : t(`greetings:languages.${resolvedLanguage.key}.explanation`);
    $[13] = resolvedLanguage.key;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const flagExplanation = t6;
  const t7 = supporterName ?? "";
  const t8 = !isGreetingReady || !hasSelectedCurrentGreeting;
  let t9;
  if ($[16] !== explanationIntro || $[17] !== flagExplanation || $[18] !== getFlagTooltip || $[19] !== isNameError || $[20] !== languages || $[21] !== resolvedLanguage || $[22] !== subtitle || $[23] !== t7 || $[24] !== t8) {
    t9 = {
      name: t7,
      selectedLanguage: resolvedLanguage,
      greeting: resolvedLanguage.greeting,
      isLoadingGreeting: t8,
      hasGreetingError: isNameError,
      languages,
      subtitle,
      flagExplanation,
      explanationIntro,
      getFlagTooltip
    };
    $[16] = explanationIntro;
    $[17] = flagExplanation;
    $[18] = getFlagTooltip;
    $[19] = isNameError;
    $[20] = languages;
    $[21] = resolvedLanguage;
    $[22] = subtitle;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
function _temp(s) {
  return getGreetingCountryCodeForParticipant(s.participant?.country, s.participant?.localId ?? undefined);
}
function _temp2(countryCode) {
  return countryCode !== "";
}